import React, { ChangeEvent, useCallback, useContext, useEffect } from 'react';

import { observer } from 'mobx-react-lite';

import useForm, { FieldValues } from 'hooks/useForm';
import TextInput from 'components/common/TextInput/TextInput';
import {
  getMessageForInvalidFields,
  isValidEmail,
  isValidPhone,
  validateRequiredField,
  isValidTaxId,
  isValidYearsOfBusiness,
  isValidUrl,
} from 'common/utils/validation.utils';
import InputSelect from 'components/common/InputSelect/InputSelect';
import PhoneNumberInput from 'components/common/PhoneNumberInput/PhoneNumberInput';
import { MultipleSelectOption } from 'components/common/MultipleSelect/MultipleSelect';
import { STATES } from 'constants/address.constants';
import MultipleSelect from 'components/common/MultipleSelect';
import Button from 'components/common/Button/Button';
import { einMask, removeNonDigits } from 'common/utils/string.utils';
import { DateFormats } from 'common/dates/date.enums';
import { isValidDateBeforeNow } from 'common/dates/date.utils';
import { RootStoreContext } from 'index';
import { notify } from 'components/common/Toast/toast.helpers';
import { ToastMessage } from 'components/common/Toast/toast.enums';

import styles from '../RegistrationProcess.module.scss';
import { BusinessInformationFormInput, PrequalificationStep } from '../registrationProcess.enums';
import {
  BUSINESS_ENTITY_STRUCTURE_OPTIONS,
  BUSINESS_INFORMATION_FORM_DATA_SESSION_STORAGE_KEY,
  CONFLICT_ERROR_STATUS_CODE,
  FEDERAL_TAX_ID_WITH_DASH_MAX_LENGTH,
} from '../registrationProcess.constants';
import { YEAR_INCORPORATED_MAX_LENGTH } from './registrationSteps.constants';

const BusinessInformation = () => {
  const { prequalificationStore } = useContext(RootStoreContext);
  const {
    prequalificationInformation,
    updatePrequalificationInformation,
    intermediaryApplicationId,
    setNextStepByCurrentStep,
  } = prequalificationStore;

  const { errors, values, setValue, handleSubmit, validateField, resetForm } = useForm<FieldValues>({
    defaultValues: {
      [BusinessInformationFormInput.BusinessName]: prequalificationInformation?.businessName || '',
      [BusinessInformationFormInput.LegalBusinessName]: prequalificationInformation?.legalBusinessName || '',
      [BusinessInformationFormInput.BusinessEntityStructure]:
        prequalificationInformation?.businessEntityStructure || '',
      [BusinessInformationFormInput.FederalTaxId]: einMask(prequalificationInformation?.federalTaxId || ''),
      [BusinessInformationFormInput.YearsInBusiness]: prequalificationInformation?.yearsInBusiness || '',
      [BusinessInformationFormInput.YearIncorporated]: prequalificationInformation?.yearIncorporated || '',
      [BusinessInformationFormInput.PhoneNumber]: prequalificationInformation?.mainBusinessPhoneNumber || '',
      [BusinessInformationFormInput.MainBusinessEmail]: prequalificationInformation?.mainBusinessEmail || '',
      [BusinessInformationFormInput.BusinessWebsite]: prequalificationInformation?.businessWebsite || '',
      [BusinessInformationFormInput.StatesWhereOperate]: prequalificationInformation?.statesOfOperation || [],
    },
    handleValidation: (fieldName, value) => {
      if (!!value) {
        switch (fieldName) {
          case BusinessInformationFormInput.FederalTaxId: {
            return isValidTaxId(value);
          }
          case BusinessInformationFormInput.YearsInBusiness: {
            return isValidYearsOfBusiness(value);
          }
          case BusinessInformationFormInput.YearIncorporated: {
            return isValidDateBeforeNow(value, DateFormats.Year)
              ? ''
              : getMessageForInvalidFields(BusinessInformationFormInput.YearIncorporated);
          }
          case BusinessInformationFormInput.PhoneNumber: {
            return isValidPhone(value);
          }
          case BusinessInformationFormInput.MainBusinessEmail: {
            return isValidEmail(value);
          }
          case BusinessInformationFormInput.BusinessWebsite: {
            return value ? isValidUrl(value) : '';
          }
        }
      } else {
        switch (fieldName) {
          case BusinessInformationFormInput.BusinessName: {
            return validateRequiredField(value, fieldName);
          }
          case BusinessInformationFormInput.PhoneNumber: {
            return validateRequiredField(value, fieldName) || isValidPhone(value);
          }
        }
      }

      return '';
    },
  });

  useEffect(() => {
    const formData = sessionStorage.getItem(BUSINESS_INFORMATION_FORM_DATA_SESSION_STORAGE_KEY);

    if (formData) {
      resetForm(JSON.parse(formData));

      sessionStorage.removeItem(BUSINESS_INFORMATION_FORM_DATA_SESSION_STORAGE_KEY);
    }
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const { name, value } = event.target;

      setValue(name, value);
    },
    [setValue],
  );

  const handleBlur = useCallback(
    (event: any): void => {
      const name = event.target.name as keyof typeof values;

      validateField(name, values[name]);
    },
    [validateField, values],
  );

  const handleSaveInformation = handleSubmit(async () => {
    try {
      sessionStorage.setItem(BUSINESS_INFORMATION_FORM_DATA_SESSION_STORAGE_KEY, JSON.stringify(values));

      await updatePrequalificationInformation(intermediaryApplicationId!, {
        businessName: values[BusinessInformationFormInput.BusinessName],
        legalBusinessName: values[BusinessInformationFormInput.LegalBusinessName] || null,
        businessEntityStructure: values[BusinessInformationFormInput.BusinessEntityStructure] || null,
        federalTaxId: values[BusinessInformationFormInput.FederalTaxId]?.replace('-', '') || null,
        yearsInBusiness: values[BusinessInformationFormInput.YearsInBusiness]?.toString() || null,
        yearIncorporated: values[BusinessInformationFormInput.YearIncorporated]?.toString() || null,
        mainBusinessPhoneNumber: values[BusinessInformationFormInput.PhoneNumber] || null,
        mainBusinessEmail: values[BusinessInformationFormInput.MainBusinessEmail] || null,
        businessWebsite: values[BusinessInformationFormInput.BusinessWebsite] || null,
        statesOfOperation: values[BusinessInformationFormInput.StatesWhereOperate] || [],
        stepType: PrequalificationStep.BusinessInformation,
      });

      sessionStorage.removeItem(BUSINESS_INFORMATION_FORM_DATA_SESSION_STORAGE_KEY);

      setNextStepByCurrentStep(PrequalificationStep.BusinessInformation);
    } catch (err: any) {
      if (err.statusCode === CONFLICT_ERROR_STATUS_CODE) {
        notify(err.message, 'error');
      } else {
        notify(ToastMessage.DefaultError, 'error');
      }
    }
  });

  return (
    <div className={styles.infoContainer}>
      <div className={styles.mainHeader}>Business Information</div>
      <div className={styles.formInput}>
        <TextInput
          useRequiredSymbol
          className={styles.input}
          containerClassName={styles.inputContainer}
          labelTitle={BusinessInformationFormInput.BusinessName}
          name={BusinessInformationFormInput.BusinessName}
          value={values[BusinessInformationFormInput.BusinessName]}
          errorMessage={errors[BusinessInformationFormInput.BusinessName]}
          onChange={handleChange}
        />
        <TextInput
          useRequiredSymbol
          className={styles.input}
          containerClassName={styles.inputContainer}
          labelTitle={BusinessInformationFormInput.LegalBusinessName}
          name={BusinessInformationFormInput.LegalBusinessName}
          value={values[BusinessInformationFormInput.LegalBusinessName]}
          errorMessage={errors[BusinessInformationFormInput.LegalBusinessName]}
          onChange={handleChange}
        />
      </div>
      <div className={styles.formInput}>
        <InputSelect
          useRequiredSymbol
          items={BUSINESS_ENTITY_STRUCTURE_OPTIONS}
          currentValue={values[BusinessInformationFormInput.BusinessEntityStructure]}
          onChange={(value) => setValue(BusinessInformationFormInput.BusinessEntityStructure, value)}
          label={BusinessInformationFormInput.BusinessEntityStructure}
          errorMessage={errors[BusinessInformationFormInput.BusinessEntityStructure]}
        />
        <TextInput
          useRequiredSymbol
          className={styles.input}
          containerClassName={styles.inputContainer}
          labelTitle={BusinessInformationFormInput.FederalTaxId}
          name={BusinessInformationFormInput.FederalTaxId}
          maxLength={FEDERAL_TAX_ID_WITH_DASH_MAX_LENGTH}
          value={einMask(values[BusinessInformationFormInput.FederalTaxId])}
          errorMessage={errors[BusinessInformationFormInput.FederalTaxId]}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>
      <div className={styles.formInput}>
        <TextInput
          useRequiredSymbol
          className={styles.input}
          containerClassName={styles.inputContainer}
          labelTitle={BusinessInformationFormInput.YearsInBusiness}
          name={BusinessInformationFormInput.YearsInBusiness}
          value={removeNonDigits(values[BusinessInformationFormInput.YearsInBusiness])}
          errorMessage={errors[BusinessInformationFormInput.YearsInBusiness]}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <TextInput
          useRequiredSymbol
          className={styles.input}
          containerClassName={styles.inputContainer}
          labelTitle={BusinessInformationFormInput.YearIncorporated}
          name={BusinessInformationFormInput.YearIncorporated}
          value={removeNonDigits(values[BusinessInformationFormInput.YearIncorporated])}
          errorMessage={errors[BusinessInformationFormInput.YearIncorporated]}
          maxLength={YEAR_INCORPORATED_MAX_LENGTH}
          placeholder="YYYY"
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>
      <div className={styles.formInput}>
        <PhoneNumberInput
          useRequiredSymbol
          className={styles.input}
          containerClassName={styles.inputContainer}
          labelTitle={BusinessInformationFormInput.PhoneNumber}
          onChange={(value: any) => setValue(BusinessInformationFormInput.PhoneNumber, value)}
          data-label={BusinessInformationFormInput.PhoneNumber}
          value={values[BusinessInformationFormInput.PhoneNumber]}
          errorMessage={errors[BusinessInformationFormInput.PhoneNumber]}
          onBlur={handleBlur}
        />
        <TextInput
          useRequiredSymbol
          className={styles.input}
          containerClassName={styles.inputContainer}
          labelTitle={BusinessInformationFormInput.MainBusinessEmail}
          name={BusinessInformationFormInput.MainBusinessEmail}
          value={values[BusinessInformationFormInput.MainBusinessEmail]}
          errorMessage={errors[BusinessInformationFormInput.MainBusinessEmail]}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>
      <div className={styles.formInput}>
        <TextInput
          className={styles.input}
          containerClassName={styles.inputContainer}
          labelTitle={BusinessInformationFormInput.BusinessWebsite}
          name={BusinessInformationFormInput.BusinessWebsite}
          value={values[BusinessInformationFormInput.BusinessWebsite]}
          errorMessage={errors[BusinessInformationFormInput.BusinessWebsite]}
          onChange={handleChange}
        />
        <MultipleSelect
          id="states"
          useRequiredSymbol
          selectOptions={STATES}
          selectAllLabel="All States"
          useSelectAll
          disabled={false}
          errorMessage={errors[BusinessInformationFormInput.StatesWhereOperate]}
          inputLabel={BusinessInformationFormInput.StatesWhereOperate}
          initialValues={
            values[BusinessInformationFormInput.StatesWhereOperate].map((stateOperation: string) =>
              STATES.find((option) => option.value === stateOperation),
            ) as MultipleSelectOption[]
          }
          onChange={(options) => {
            setValue(
              BusinessInformationFormInput.StatesWhereOperate,
              options.map((option) => option.value),
            );
          }}
        />
      </div>
      <div className={styles.formInput}>
        <Button onClick={handleSaveInformation} className={styles.saveButton}>
          Save and Continue
        </Button>
      </div>
    </div>
  );
};

export default observer(BusinessInformation);
